import { validateRecoveryBundle } from './lifecycleRecoveryStore';

// No default transport: future cutover must bind isolated lifecycle HTTP calls,
// without global auth interceptors, request logging, or credential telemetry.
// initial/status/replay return { status: HTTP status, data: parsed JSON }.
export function createLifecycleRecoveryClient({ store, transport, clearOrdinaryAuth }) {
  const headers = (bundle, replay) => ({
    'Account-Lifecycle-Operation-Id': bundle.operationId,
    'Account-Lifecycle-Credential': bundle.credential,
    ...(replay ? { 'Idempotency-Key': bundle.operationKey } : {}),
  });
  const receive = async (response, bundle) => {
    if (response?.status === 410 && response.data?.code === 'LIFECYCLE_AUTHORITY_EXPIRED') {
      try { await store.remove(bundle.operationId); } catch (_) { return { state: 'expired', cleanupPending: true }; }
      return { state: 'expired' };
    }
    const p = response?.data;
    if (![200, 202].includes(response?.status) || p?.operationId !== bundle.operationId ||
        !['pending', 'completed', 'needs_attention'].includes(p.status) ||
        !p.requestedAt || !Number.isFinite(Date.parse(p.requestedAt)) ||
        (p.status === 'completed' && (!p.completedAt || !Number.isFinite(Date.parse(p.completedAt))))) {
      return { state: response?.status === 401 ? 'unavailable' : 'uncertain' };
    }
    // Recovery material is already durable; clear only ordinary auth, never IDB.
    await clearOrdinaryAuth({ status: p.status });
    return { state: p.status };
  };
  const request = async (kind, bundle) => {
    try { return await receive(await transport[kind]({ headers: headers(bundle, kind !== 'status') }), bundle); }
    catch (_) { return { state: 'uncertain' }; }
  };
  const recover = async () => {
    let bundle;
    try { bundle = await store.read(); if (bundle) validateRecoveryBundle(bundle); }
    catch (_) { return { state: 'unavailable' }; }
    if (!bundle) return { state: 'unavailable' };
    // Read-only replay first checks the exact retained request key. Status is
    // also read-only, and never creates an operation if replay is unavailable.
    const result = await request('replay', bundle);
    return result.state === 'uncertain' ? request('status', bundle) : result;
  };
  return {
    recover,
    initiate: async () => {
      let bundle, claimed;
      try {
        bundle = validateRecoveryBundle(await store.prepare());
        claimed = await store.claimDispatch(bundle.operationId);
      } catch (_) { return { state: 'storage_unavailable' }; }
      // A durable dispatch marker prevents a second creation after response
      // loss/reload/two tabs. Crash before dispatch also takes read-only recovery.
      if (!claimed) return recover();
      const result = await request('initial', bundle);
      return result.state === 'uncertain' ? recover() : result;
    },
    forget: async () => {
      try { await store.remove(); return { state: 'unavailable' }; }
      catch (_) { return { state: 'removal_failed' }; }
    },
  };
}
